import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

export const OffersManager = () => {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", active: true });

  const load = async () => {
    const { data } = await api.get("/admin/offers");
    setOffers(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/offers/${editing.id}`, form);
      else await api.post("/admin/offers", form);
      toast.success(editing ? "Offer updated" : "Offer added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (offer) => {
    if (!window.confirm(`Remove "${offer.title}"?`)) return;
    await api.delete(`/admin/offers/${offer.id}`);
    toast.success("Offer removed");
    load();
  };

  return (
    <div data-testid="admin-offers-manager">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-cream">Offers</h2>
        <Button data-testid="admin-add-offer-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel"
          onClick={() => { setEditing(null); setForm({ title: "", description: "", active: true }); setOpen(true); }}>
          <Plus className="mr-1.5 h-4 w-4" /> Add Offer
        </Button>
      </div>

      <div className="mt-6 space-y-2">
        {offers.map((o) => (
          <div key={o.id} data-testid={`admin-offer-row-${o.id}`}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-4">
            <div className="min-w-[180px] flex-1">
              <p className="text-sm font-semibold text-cream">{o.title}</p>
              <p className="text-xs text-muted-foreground">{o.description}</p>
            </div>
            <span className={`text-xs uppercase tracking-[0.15em] ${o.active ? "text-caramel" : "text-muted-foreground"}`}>
              {o.active ? "Live" : "Paused"}
            </span>
            <Button size="icon" variant="ghost" data-testid={`admin-edit-offer-${o.id}`}
              onClick={() => { setEditing(o); setForm({ title: o.title, description: o.description, active: o.active }); setOpen(true); }}>
              <Pencil className="h-4 w-4 text-cream/80" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => remove(o)} data-testid={`admin-delete-offer-${o.id}`}>
              <Trash2 className="h-4 w-4 text-red-400" />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-offer-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit Offer" : "Add Offer"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title} required data-testid="offer-title-input" className="mt-1.5"
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} rows={2} data-testid="offer-description-input" className="mt-1.5"
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm text-cream">Active</span>
              <Switch checked={form.active} data-testid="offer-active-switch"
                onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="offer-save-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
